/**
 * SEO Meta Tag Utilities
 * Provides helpers for generating consistent meta tags across routes
 */

import { SEO_CONFIG } from "./seo-constants";

export interface SeoMetaConfig {
  title: string;
  description: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: "website" | "article";
  canonicalUrl?: string;
  noindex?: boolean;
}

const { SITE_NAME: DEFAULT_SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE } = SEO_CONFIG;

/**
 * Generate comprehensive SEO meta tags for a route
 */
export function generateSeoMeta(config: SeoMetaConfig) {
  const {
    title,
    description,
    keywords = [],
    ogImage = DEFAULT_OG_IMAGE,
    ogType = "website",
    canonicalUrl,
    noindex = false,
  } = config;

  const fullTitle = title.includes(DEFAULT_SITE_NAME)
    ? title
    : `${title} | ${DEFAULT_SITE_NAME}`;

  const metaTags: Array<
    | { title: string }
    | { name: string; content: string }
    | { property: string; content: string }
    | { tagName: string; rel: string; href: string }
  > = [{ title: fullTitle }, { name: "description", content: description }];

  // Keywords
  if (keywords.length > 0) {
    metaTags.push({ name: "keywords", content: keywords.join(", ") });
  }

  // Robots
  if (noindex) {
    metaTags.push({ name: "robots", content: "noindex, nofollow" });
  }

  // Open Graph - required for social media previews
  const fullImageUrl = ogImage.startsWith("http")
    ? ogImage
    : `${SITE_URL}${ogImage}`;
  const fullCanonicalUrl = canonicalUrl
    ? canonicalUrl.startsWith("http")
      ? canonicalUrl
      : `${SITE_URL}${canonicalUrl}`
    : SITE_URL;

  metaTags.push(
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:type", content: ogType },
    { property: "og:url", content: fullCanonicalUrl },
    { property: "og:image", content: fullImageUrl },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: fullTitle },
    { property: "og:site_name", content: DEFAULT_SITE_NAME },
    { property: "og:locale", content: "uk_UA" },
  );

  // Twitter Card
  metaTags.push(
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: fullImageUrl },
    { name: "twitter:image:alt", content: fullTitle },
  );

  // Canonical URL
  if (canonicalUrl) {
    metaTags.push({
      tagName: "link",
      rel: "canonical",
      href: canonicalUrl.startsWith("http")
        ? canonicalUrl
        : `${SITE_URL}${canonicalUrl}`,
    });
  }

  return metaTags;
}

/**
 * Generate JSON-LD structured data for organization
 */
export function generateOrganizationSchema() {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: DEFAULT_SITE_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/icon.svg`,
      description:
        "Система контролю торгових марок. Ви вводите номер — система знаходить статус у реєстрі, показує стан діловодства й підказує, що робити далі. Нагадує про дедлайни в Telegram.",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Customer Support",
        url: `${SITE_URL}/support`,
      },
    },
  };
}

/**
 * Generate JSON-LD structured data for web application
 */
export function generateWebApplicationSchema() {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: DEFAULT_SITE_NAME,
      url: SITE_URL,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web Browser",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "UAH",
      },
    },
  };
}

/**
 * Generate JSON-LD structured data for WebSite with search action
 * Helps Google understand your brand and site structure
 */
export function generateWebSiteSchema() {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: DEFAULT_SITE_NAME,
      alternateName: ["Брендлі", "Brandly.my"],
      url: SITE_URL,
      description: "Система контролю торгових марок для українського бізнесу",
      inLanguage: "uk-UA",
      publisher: {
        "@type": "Organization",
        name: DEFAULT_SITE_NAME,
        url: SITE_URL,
      },
    },
  };
}

/**
 * Generate JSON-LD breadcrumb schema for better navigation signals
 */
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>,
) {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
      })),
    },
  };
}
